import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { produtos, empresa } from '../data/mock';

const ProdutoDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const produto = produtos.find(p => p.id === parseInt(id));
  
  const [imagemAtual, setImagemAtual] = useState(0);

  if (!produto) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Produto não encontrado</h2>
          <Button onClick={() => navigate('/loja')}>Voltar para Loja</Button>
        </div>
      </div>
    );
  }

  // Array de imagens - usa o array 'imagens' se existir, senão usa apenas a imagem principal
  const todasImagens = produto.imagens || [produto.imagem];

  const handleWhatsApp = () => {
    const mensagem = `Olá! Tenho interesse no ${produto.nome}.\n\nPreço à vista: R$ ${produto.preco_vista.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}\nParcelado: ${produto.parcelas}x de R$ ${(produto.preco_parcelado / produto.parcelas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })} sem juros\n\nGostaria de mais informações.`;
    const texto = encodeURIComponent(mensagem);
    window.open(`${empresa.contato.whatsapp_link}?text=${texto}`, '_blank');
  };

  const proximaImagem = () => {
    setImagemAtual((prev) => (prev + 1) % todasImagens.length);
  };

  const imagemAnterior = () => {
    setImagemAtual((prev) => (prev - 1 + todasImagens.length) % todasImagens.length);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Breadcrumb */}
      <section className="bg-white border-b py-4">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-purple-600">Início</Link>
            <span>/</span>
            <Link to="/loja" className="hover:text-purple-600">Loja</Link>
            <span>/</span>
            <span className="text-slate-900 font-medium">{produto.nome}</span>
          </div>
        </div>
      </section>

      {/* Botão Voltar */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="outline"
            onClick={() => navigate('/loja')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Loja
          </Button>
        </div>
      </section>

      {/* Conteúdo Principal */}
      <section className="py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Galeria de Imagens */}
            <div>
              <Card className="overflow-hidden shadow-xl">
                <div className="relative aspect-[3/2] bg-slate-100">
                  <img
                    src={todasImagens[imagemAtual]}
                    alt={`${produto.nome} - Foto ${imagemAtual + 1}`}
                    className="w-full h-full object-contain"
                  />
                  
                  {/* Controles da galeria */}
                  {todasImagens.length > 1 && (
                    <>
                      <button
                        onClick={imagemAnterior}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      >
                        <ChevronLeft className="w-6 h-6 text-slate-900" />
                      </button>
                      <button
                        onClick={proximaImagem}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                      >
                        <ChevronRight className="w-6 h-6 text-slate-900" />
                      </button>

                      {/* Indicador de posição */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {todasImagens.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setImagemAtual(index)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              index === imagemAtual
                                ? 'bg-white w-8'
                                : 'bg-white/50 hover:bg-white/75'
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {/* Badge disponível */}
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-green-600 text-white">Disponível</Badge>
                  </div>
                </div>
              </Card>

              {/* Miniaturas */}
              {todasImagens.length > 1 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {todasImagens.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setImagemAtual(index)}
                      className={`aspect-[4/3] rounded-lg overflow-hidden border-2 transition-all ${
                        index === imagemAtual
                          ? 'border-purple-600 ring-2 ring-purple-200'
                          : 'border-slate-200 hover:border-purple-400'
                      }`}
                    >
                      <img
                        src={img}
                        alt={`Miniatura ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Informações do Produto */}
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                {produto.nome}
              </h1>
              
              <p className="text-xl text-gray-600 mb-6">
                {produto.descricao}
              </p>

              {/* Preço */}
              <Card className="mb-8 bg-gradient-to-br from-purple-50 to-cyan-50 border-2 border-purple-200">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">Preço à vista</p>
                    <p className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-cyan-600 bg-clip-text text-transparent">
                      R$ {produto.preco_vista.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                  
                  <div className="pt-4 border-t border-purple-200">
                    <p className="text-sm text-gray-600 mb-1">
                      ou R$ {produto.preco_parcelado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} em {produto.parcelas}x sem juros
                    </p>
                    <p className="text-lg text-slate-900">
                      {produto.parcelas}x de <span className="font-bold text-purple-600">
                        R$ {(produto.preco_parcelado / produto.parcelas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Especificações Técnicas */}
              <Card className="mb-8">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">
                    Especificações Técnicas
                  </h3>
                  
                  <div className="space-y-4">
                    {produto.processador && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Processador</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.processador}</span>
                      </div>
                    )}
                    
                    {produto.ram && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Memória RAM</span>
                        <span className="text-slate-900 font-semibold">{produto.ram}</span>
                      </div>
                    )}
                    
                    {produto.armazenamento && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Armazenamento</span>
                        <span className="text-slate-900 font-semibold">{produto.armazenamento}</span>
                      </div>
                    )}
                    
                    {produto.tela && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Tela</span>
                        <span className="text-slate-900 font-semibold">{produto.tela}</span>
                      </div>
                    )}
                    
                    {produto.placa_video_dedicada && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">GPU Dedicada</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.placa_video_dedicada}</span>
                      </div>
                    )}
                    
                    {produto.placa_video_integrada && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">GPU Integrada</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.placa_video_integrada}</span>
                      </div>
                    )}
                    
                    {produto.placa_video && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Placa de Vídeo</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.placa_video}</span>
                      </div>
                    )}
                    
                    {produto.teclado_numerico && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Teclado Numérico</span>
                        <span className="text-slate-900 font-semibold">{produto.teclado_numerico}</span>
                      </div>
                    )}
                    
                    {produto.entradas && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Entradas</span>
                        <span className="text-slate-900 font-semibold">{produto.entradas}</span>
                      </div>
                    )}
                    
                    {produto.teclado && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Teclado</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.teclado}</span>
                      </div>
                    )}
                    
                    {produto.mouse && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Mouse</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.mouse}</span>
                      </div>
                    )}
                    
                    {produto.fone && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Fone</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.fone}</span>
                      </div>
                    )}
                    
                    {produto.mousepad && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Mouse Pad</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.mousepad}</span>
                      </div>
                    )}
                    
                    {produto.extras && (
                      <div className="flex justify-between py-3 border-b">
                        <span className="text-gray-600 font-medium">Extras</span>
                        <span className="text-slate-900 font-semibold text-right">{produto.extras}</span>
                      </div>
                    )}
                    
                    {produto.observacoes && (
                      <div className="flex justify-between py-3 border-b bg-red-50">
                        <span className="text-gray-600 font-medium">⚠️ Observações</span>
                        <span className="text-red-600 font-bold text-right">{produto.observacoes}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Botão de Compra */}
              <Button
                size="lg"
                className="w-full bg-green-600 hover:bg-green-700 text-white py-8 text-xl shadow-lg hover:shadow-xl transition-all"
                onClick={handleWhatsApp}
              >
                <ShoppingCart className="w-6 h-6 mr-3" />
                Comprar via WhatsApp
              </Button>

              {/* Informações Adicionais */}
              <div className="mt-6 grid grid-cols-3 gap-4 text-center">
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-2xl mb-1">🛡️</p>
                  <p className="text-xs text-gray-600">Garantia</p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-2xl mb-1">💳</p>
                  <p className="text-xs text-gray-600">Parcelamento</p>
                </div>
                <div className="p-4 bg-white rounded-lg">
                  <p className="text-2xl mb-1">🤝</p>
                  <p className="text-xs text-gray-600">Suporte</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProdutoDetalhes;
